/**
 * BranchVersionDialog — fork a candidate :Circuit off a source. Per IR
 * plan §8.6 + Phase 14 (`POST /circuits/{src}/branch`).
 *
 * The dialog has two modes:
 *   * `sourceCircuitId` provided  → real HTTP call via useBranchCircuit
 *   * no `sourceCircuitId`        → falls back to clipboard staging,
 *                                    used by version-row callers that
 *                                    don't yet know which Circuit a
 *                                    given version belongs to (the
 *                                    legacy /projects/{pid}/versions
 *                                    response is project-keyed, not
 *                                    circuit-keyed).
 *
 * Once every caller threads the source circuit id, the clipboard
 * fallback can come out.
 */
import { useBranchCircuit } from "@features/circuits/hooks/useCircuitLifecycle";
import BranchIcon from "@mui/icons-material/CallSplitRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "@stores/toastStore";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

interface BranchVersionDialogProps {
  open: boolean;
  /** The project / source-circuit name — display only. */
  projectName: string;
  /** The version label/id branched from — display only. */
  sourceVersionLabel: string;
  /** Source Circuit id. When set, the dialog calls the real
   *  endpoint; absent → clipboard stub. */
  sourceCircuitId?: string;
  onClose: () => void;
  /** Optional callback after a successful branch — receives the new
   *  circuit's id. Lets callers (like CircuitsTab) close the dialog
   *  + react to the new state. */
  onBranched?: (newCircuitId: string) => void;
}

export function BranchVersionDialog({
  open,
  projectName,
  sourceVersionLabel,
  sourceCircuitId,
  onClose,
  onBranched,
}: BranchVersionDialogProps) {
  // Outer renders only the Dialog wrapper. The body holds the form state
  // and mounts/unmounts with ``open`` (MUI Dialog default), so each open
  // starts with the useState initial values — no reset useEffect needed.
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <BranchVersionDialogBody
        projectName={projectName}
        sourceVersionLabel={sourceVersionLabel}
        sourceCircuitId={sourceCircuitId}
        onClose={onClose}
        onBranched={onBranched}
      />
    </Dialog>
  );
}

interface BranchVersionDialogBodyProps {
  projectName: string;
  sourceVersionLabel: string;
  sourceCircuitId?: string;
  onClose: () => void;
  onBranched?: (newCircuitId: string) => void;
}

function BranchVersionDialogBody({
  projectName,
  sourceVersionLabel,
  sourceCircuitId,
  onClose,
  onBranched,
}: BranchVersionDialogBodyProps) {
  const navigate = useNavigate();
  const branch = useBranchCircuit();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const isLive = !!sourceCircuitId;

  function handleStage() {
    const candidate = name.trim() || `${projectName}-candidate`;
    const prompt = [
      `Branch a candidate circuit from ${projectName} ${sourceVersionLabel}.`,
      `Name: ${candidate}.`,
      description.trim() ? `Description: ${description.trim()}.` : "",
      `Use kind="candidate", status="draft", parent_circuit_id=<source>.`,
    ]
      .filter(Boolean)
      .join("\n");
    void navigator.clipboard.writeText(prompt).then(
      () => toast.success("Branch prompt copied — paste it into chat to apply"),
      () => toast.error("Could not copy to clipboard"),
    );
    onClose();
  }

  function handleBranch() {
    if (!sourceCircuitId) return;
    branch.mutate(
      {
        sourceCircuitId,
        payload: {
          name: name.trim() || `${projectName}-candidate`,
          description: description.trim() || undefined,
        },
      },
      {
        onSuccess: (data) => {
          onBranched?.(data.new_circuit.id);
          onClose();
          // Route to the new circuit's project chat — gives the user
          // somewhere to *do* something with the branch they just made.
          void navigate({
            to: "/projects/$projectId/chat",
            params: { projectId: data.new_circuit.project_id },
          });
        },
      },
    );
  }

  return (
    <>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BranchIcon color="secondary" fontSize="small" />
          <Box>Branch this version as a candidate</Box>
        </Stack>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Fork {projectName} {sourceVersionLabel} into a new candidate circuit.
          Versioned independently; merges back into main via the Mapping Bench
          when ready.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Candidate name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`${projectName}-candidate`}
            autoFocus
            fullWidth
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Why are you forking? Goal of this branch."
            multiline
            minRows={2}
            fullWidth
          />
          {!isLive && (
            <Alert severity="info" variant="outlined">
              This version isn't yet associated with a circuit id — copying a
              chat prompt instead. Once Versions surface their owning Circuit,
              this dialog will fire the branch endpoint directly.
            </Alert>
          )}
          {branch.error && (
            <Alert severity="error">
              {(branch.error as Error).message ?? "Branch failed"}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={branch.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={
            branch.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <BranchIcon />
            )
          }
          onClick={isLive ? handleBranch : handleStage}
          disabled={branch.isPending}
        >
          {branch.isPending ? "Branching…" : isLive ? "Branch" : "Stage branch"}
        </Button>
      </DialogActions>
    </>
  );
}
